import { Client } from '@modelcontextprotocol/sdk/client/index.js';
import { StreamableHTTPClientTransport } from '@modelcontextprotocol/sdk/client/streamableHttp.js';
import { SSEClientTransport } from '@modelcontextprotocol/sdk/client/sse.js';
import type { McpServerConfig, McpServerState, McpToolInfo } from './types';

const STORE_KEY = 'dts.mcpServers';

interface Live {
  client: Client;
  tools: McpToolInfo[];
}

/**
 * The user's MCP connections, held in this browser. Only HTTP servers work here (a web page can't start local
 * processes), and the server must allow cross-origin requests from this site.
 */
class McpManager {
  private configs: McpServerConfig[] = [];
  private live = new Map<string, Live>();
  private errors = new Map<string, string>();
  private connecting = new Map<string, Promise<Live>>();

  constructor() {
    try {
      this.configs = JSON.parse(localStorage.getItem(STORE_KEY) ?? '[]') as McpServerConfig[];
    } catch {
      this.configs = [];
    }
  }

  private persist() {
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify(this.configs));
    } catch {
      /* storage blocked */
    }
  }

  list(): McpServerState[] {
    return this.configs.map((c) => this.state(c));
  }

  private state(c: McpServerConfig): McpServerState {
    const l = this.live.get(c.id);
    return { ...c, connected: !!l, error: this.errors.get(c.id), tools: l?.tools ?? [] };
  }

  async save(config: McpServerConfig): Promise<McpServerState> {
    const i = this.configs.findIndex((c) => c.id === config.id);
    if (i >= 0) this.configs[i] = config;
    else this.configs.push(config);
    this.persist();
    await this.disconnect(config.id);
    if (config.enabled) await this.ensure(config.id).catch(() => undefined);
    return this.state(config);
  }

  async remove(id: string) {
    await this.disconnect(id);
    this.configs = this.configs.filter((c) => c.id !== id);
    this.persist();
  }

  async reconnect(id: string): Promise<McpServerState> {
    await this.disconnect(id);
    await this.ensure(id).catch(() => undefined);
    return this.state(this.configs.find((c) => c.id === id)!);
  }

  async test(config: McpServerConfig): Promise<McpToolInfo[]> {
    const live = await this.open(config);
    await live.client.close().catch(() => undefined);
    return live.tools;
  }

  /** All tools from every enabled server, connecting as needed. Servers that fail are skipped (error recorded). */
  async allTools(): Promise<{ server: McpServerConfig; tool: McpToolInfo }[]> {
    const out: { server: McpServerConfig; tool: McpToolInfo }[] = [];
    await Promise.all(
      this.configs.filter((c) => c.enabled).map(async (c) => {
        try {
          const l = await this.ensure(c.id);
          l.tools.forEach((tool) => out.push({ server: c, tool }));
        } catch {
          /* recorded in errors */
        }
      }),
    );
    return out;
  }

  async callTool(serverId: string, name: string, args: Record<string, unknown>): Promise<{ text: string; isError?: boolean }> {
    const l = await this.ensure(serverId);
    const res = (await l.client.callTool({ name, arguments: args }, undefined, { timeout: 60_000 })) as {
      content?: { type: string; text?: string; resource?: { text?: string } }[];
      structuredContent?: unknown;
      isError?: boolean;
    };
    const parts = (res.content ?? []).map((c) => c.text ?? c.resource?.text ?? (c.type !== 'text' ? `[${c.type}]` : '')).filter(Boolean);
    if (parts.length === 0 && res.structuredContent) parts.push(JSON.stringify(res.structuredContent));
    return { text: parts.join('\n'), isError: res.isError };
  }

  private async ensure(id: string): Promise<Live> {
    const existing = this.live.get(id);
    if (existing) return existing;
    let p = this.connecting.get(id);
    if (!p) {
      const config = this.configs.find((c) => c.id === id);
      if (!config) throw new Error('Unknown MCP server.');
      p = this.open(config)
        .then((l) => {
          this.live.set(id, l);
          this.errors.delete(id);
          l.client.onclose = () => this.live.delete(id);
          return l;
        })
        .catch((e: unknown) => {
          this.errors.set(id, e instanceof Error ? e.message : String(e));
          throw e;
        })
        .finally(() => this.connecting.delete(id));
      this.connecting.set(id, p);
    }
    return p;
  }

  private async open(config: McpServerConfig): Promise<Live> {
    if (config.transport === 'stdio') throw new Error('Command-line (stdio) MCP servers need the DTS Mail desktop app. Use an HTTP server here.');
    if (!config.url) throw new Error('A URL is required.');
    const url = new URL(config.url);
    const requestInit = { headers: config.headers ?? {} };
    const client = new Client({ name: 'dts-mail-web', version: __APP_VERSION__ }, { capabilities: {} });
    try {
      await client.connect(new StreamableHTTPClientTransport(url, { requestInit }));
    } catch (first) {
      const fallback = new Client({ name: 'dts-mail-web', version: __APP_VERSION__ }, { capabilities: {} });
      try {
        await fallback.connect(new SSEClientTransport(url, { requestInit }));
        return { client: fallback, tools: await listTools(fallback) };
      } catch {
        // A TypeError from fetch is almost always the server refusing cross-origin requests.
        if (first instanceof TypeError) throw new Error(`Couldn't reach ${url.host}. The server may not allow requests from this website (CORS).`);
        throw first;
      }
    }
    return { client, tools: await listTools(client) };
  }

  private async disconnect(id: string) {
    const l = this.live.get(id);
    this.live.delete(id);
    this.errors.delete(id);
    if (l) await l.client.close().catch(() => undefined);
  }
}

async function listTools(client: Client): Promise<McpToolInfo[]> {
  const tools: McpToolInfo[] = [];
  let cursor: string | undefined;
  do {
    const page = await client.listTools(cursor ? { cursor } : undefined);
    for (const t of page.tools) {
      tools.push({
        name: t.name,
        description: t.description,
        readOnly: t.annotations?.readOnlyHint,
        destructive: t.annotations?.destructiveHint,
        inputSchema: (t.inputSchema ?? { type: 'object' }) as Record<string, unknown>,
      });
    }
    cursor = page.nextCursor;
  } while (cursor && tools.length < 500);
  return tools;
}

export const mcp = new McpManager();
